import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Paginação client-side via IntersectionObserver no sentinel.
 *
 * Por que client e não server: tabs de configurações têm tipicamente <200
 * itens. Server pagination + cursor seria over-engineering pro ganho real.
 * Mantém composer/atendimento intocados (mesma queryKey, mesma resposta).
 *
 * O viewport/altura da tabela é responsabilidade do CALLER — use flex chain
 * (`flex h-full flex-col overflow-hidden` no parent + `flex min-h-0 flex-1
 * overflow-y-auto` no card) pra adaptar ao tamanho da janela, ou fixe altura
 * via CSS se preferir.
 *
 * Como usar:
 * ```tsx
 * const all = q.data ?? [];
 * const { visible, hasMore, sentinelRef } = useClientPagination(all);
 *
 * <section className="flex h-full flex-col overflow-hidden">
 *   <header className="flex-shrink-0">...</header>
 *   <div className="flex min-h-0 flex-1 flex-col overflow-y-auto ...">
 *     <table>
 *       <thead className="sticky top-0">...</thead>
 *       <tbody>
 *         {visible.map(...)}
 *         {hasMore && <tr ref={sentinelRef}>...</tr>}
 *       </tbody>
 *     </table>
 *   </div>
 * </section>
 * ```
 */
export function useClientPagination<T>(
  items: T[],
  options: {
    /** Tamanho de cada "página" client-side. Default 50. */
    pageSize?: number;
    /** Margin do IntersectionObserver — pré-carrega antes do sentinel aparecer. */
    rootMargin?: string;
  } = {},
): {
  visible: T[];
  hasMore: boolean;
  visibleCount: number;
  setVisibleCount: (n: number) => void;
  resetPagination: () => void;
  sentinelRef: React.MutableRefObject<HTMLTableRowElement | null>;
} {
  const pageSize = options.pageSize ?? 50;
  const rootMargin = options.rootMargin ?? '200px';

  const [visibleCount, setVisibleCount] = useState(pageSize);
  const visible = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);
  const hasMore = visibleCount < items.length;

  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisibleCount((c) => Math.min(c + pageSize, items.length));
          }
        }
      },
      { rootMargin, threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, items.length, pageSize, rootMargin]);

  // useCallback pra estabilidade da referência — permite usar em deps de
  // useEffect sem disparar loop.
  const resetPagination = useCallback((): void => setVisibleCount(pageSize), [pageSize]);

  return {
    visible,
    hasMore,
    visibleCount,
    setVisibleCount,
    resetPagination,
    sentinelRef,
  };
}
